// Claims desk API client — everything goes through lib/api/client.ts (error
// codes, branch header, ETag concurrency handled once there), same
// convention every other features/* module follows.
import { api } from '@/lib/api/client';
import type {
  Claim, ClaimMode, ClaimWorkspace, ClaimAuthorization, ClaimQuery, ClaimSubmission, ClaimEstimate, RunningBill,
  CreateEstimateInput, CreateAuthorizationInput, SendAuthorizationInput, DecideAuthorizationInput,
  CreateQueryInput, ReplyQueryInput, CreateSubmissionInput,
  ClaimInvoice, PostSettlementInput, PostSettlementResult,
  DocumentChecklistItem, GeneratedForm, PacketResult, ClaimFormsStatus,
} from './types';

const BASE = '/claims';

export interface ClaimListParams {
  status?: string;
  payer_id?: number;
  admission_id?: number;
  owner?: number;
  tat?: 'breached';
}

export function fetchClaims(params?: ClaimListParams): Promise<Claim[]> {
  return api.get(BASE, { query: params as Record<string, string | number | boolean | null | undefined> | undefined });
}

export interface CreateClaimInput {
  admission_id?: number;
  visit_id?: number;
  coverage_id?: number;
  payer_id: number;
  mode: ClaimMode;
  treating_doctor_user_id?: number;
  planned_admission_date?: string;
  expected_los_days?: number;
  no_preauth_reason?: string;
}

/** `POST /api/claims` — the explicit claim-creation path, used by coverage
 * capture (wireframe S1) for the one mode `ensureClaimForCoverage` can never
 * produce on its own: `'reimbursement'` (it always derives 'cashless' for a
 * scheme_code of 'insurance' — see utils/claimAutoCreate.js). Every other
 * mode is opened automatically the moment a resolved insurer/TPA is saved on
 * the coverage row, so this is reserved for that one case. */
export function createClaim(input: CreateClaimInput): Promise<Claim> {
  return api.post(BASE, input);
}

export function fetchClaim(id: number): Promise<ClaimWorkspace> {
  return api.get(`${BASE}/${id}`);
}

export function fetchRunningBill(id: number): Promise<RunningBill> {
  return api.get(`${BASE}/${id}/running-bill`);
}

export function createEstimate(claimId: number, input: CreateEstimateInput): Promise<ClaimEstimate> {
  return api.post(`${BASE}/${claimId}/estimates`, input);
}

export function createAuthorization(claimId: number, input: CreateAuthorizationInput): Promise<ClaimAuthorization> {
  return api.post(`${BASE}/${claimId}/authorizations`, input);
}

export function sendAuthorization(claimId: number, authId: number, input: SendAuthorizationInput = {}): Promise<ClaimAuthorization> {
  return api.post(`${BASE}/${claimId}/authorizations/${authId}/send`, input);
}

export function decideAuthorization(
  claimId: number, authId: number, input: DecideAuthorizationInput
): Promise<{ authorization: ClaimAuthorization; claim: Claim }> {
  return api.post(`${BASE}/${claimId}/authorizations/${authId}/decision`, input);
}

export function createQuery(claimId: number, input: CreateQueryInput): Promise<ClaimQuery> {
  return api.post(`${BASE}/${claimId}/queries`, input);
}

export function replyQuery(claimId: number, queryId: number, input: ReplyQueryInput): Promise<ClaimQuery> {
  return api.post(`${BASE}/${claimId}/queries/${queryId}/reply`, input);
}

export function createSubmission(claimId: number, input: CreateSubmissionInput): Promise<ClaimSubmission> {
  return api.post(`${BASE}/${claimId}/submissions`, input);
}

export function transitionClaim(claimId: number, to: string, extra?: Record<string, unknown>): Promise<Claim> {
  return api.post(`${BASE}/${claimId}/transition`, { to, ...extra });
}

export function fetchClaimInvoices(claimId: number): Promise<ClaimInvoice[]> {
  return api.get(`${BASE}/${claimId}/invoices`);
}

export function postSettlement(claimId: number, input: PostSettlementInput): Promise<PostSettlementResult> {
  return api.post(`${BASE}/${claimId}/settlements`, input);
}

// ── Insurance Claims Phase 5 (§6, §7) — documents, forms, packet ─────────

export function fetchClaimDocuments(claimId: number): Promise<DocumentChecklistItem[]> {
  return api.get(`${BASE}/${claimId}/documents`);
}

export function attachClaimDocument(claimId: number, docKey: string, documentId: number): Promise<DocumentChecklistItem> {
  return api.post(`${BASE}/${claimId}/documents`, { doc_key: docKey, document_id: documentId });
}

export function waiveClaimDocument(claimId: number, checklistId: number, waive: boolean, waivedReason?: string): Promise<DocumentChecklistItem> {
  return api.patch(`${BASE}/${claimId}/documents/${checklistId}`, { waive, waived_reason: waivedReason });
}

export function generateClaimForm(claimId: number, form: 'preauth' | 'part_b'): Promise<GeneratedForm> {
  return api.post(`${BASE}/${claimId}/forms`, { form });
}

export function buildClaimPacket(claimId: number, stage: 'claim' | 'final', format: 'pdf' | 'zip'): Promise<PacketResult> {
  return api.post(`${BASE}/${claimId}/packet`, { stage, format });
}

/** Read-only: last generation per form, live missing fields (with where to
 * fix each), the latest packet and the data-sharing consent state. */
export function fetchClaimFormsStatus(claimId: number): Promise<ClaimFormsStatus> {
  return api.get(`${BASE}/${claimId}/forms`);
}

/** PATCH /claims/:id — the desk-editable fields (treating doctor, payer's own
 * claim no., expected LOS, owner). */
export function updateClaim(claimId: number, patch: {
  treating_doctor_user_id?: number | null; external_claim_no?: string | null; expected_los_days?: number | null; owner_user_id?: number | null;
}): Promise<Claim> {
  return api.patch(`${BASE}/${claimId}`, patch);
}
